import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Zap, Check, ArrowLeft, ArrowRight, Send, ShieldCheck, AlertCircle } from "lucide-react";
import ActionFormFields from "./ActionFormFields";
import EmployeeSummaryCard from "./EmployeeSummaryCard";
import { createBulkStyles, BULK_CSS } from "./BulkActions/bulkActions.styles";
import { Btn } from "./BulkActions/BulkSteps";
import { getWorkflowApprovalLabel, isActionBlockedForManagerView } from "../Constant/EmployeeActions.config";
import { getDisplayError } from "../../../utils/displayError";

const STEPS = ["choose", "details", "review"];
const SALARY_ACTIONS = ["promote", "demote", "rehire", "salary_change"];

// Option id -> translation key, for the dropdown values shown on the review step
// (same option lists as ActionFormFields).
const OPTION_FIELDS = [
  {
    field: "transferType",
    labelKey: "transferType",
    options: { permanent: "permanentTransfer", temporary: "temporaryTransfer", project_based: "projectBased" },
  },
  {
    field: "salaryAdjustmentType",
    labelKey: "adjustmentType",
    options: {
      annual_increase: "annualIncrease",
      promotion: "promotion",
      market_adjustment: "marketAdjustment",
      performance_bonus: "performanceBased",
      cost_of_living: "costOfLiving",
      correction: "salaryCorrection",
    },
  },
  {
    field: "demotionReason",
    labelKey: "demotionReasonCategory",
    options: {
      performance: "performanceIssues",
      restructuring: "companyRestructuring",
      misconduct: "misconduct",
      business_needs: "businessNeeds",
      other: "other",
    },
  },
  {
    field: "terminationType",
    labelKey: "terminationType",
    options: {
      voluntary: "voluntaryResignation",
      involuntary: "involuntaryTermination",
      retirement: "retirement",
      contract_end: "contractEnd",
      mutual_agreement: "mutualAgreement",
      layoff: "layoff",
    },
  },
  {
    field: "suspensionType",
    labelKey: "suspensionType",
    options: { with_pay: "withPay", without_pay: "withoutPay", partial_pay: "partialPay" },
  },
];

/**
 * Single-employee action wizard, in the Bulk Actions look.
 *
 * Layout: a fixed side panel (employee details + chosen action + its approval
 * path) next to the current step — 1) choose the action, 2) fill its details,
 * 3) review & submit — so everything stays in view without scrolling.
 */
const ActionModal = ({
  t,
  styles,
  theme,
  isRTL,
  language,
  selectedEmployee,
  selectedAction,
  setSelectedAction,
  formData,
  errorMsg,
  setErrorMsg,
  departments,
  departmentManagers,
  getEmployeeName,
  handleInputChange,
  handleDropdownChange,
  onReview,
  onConfirm,
  isSubmitting,
  isAdminView,
  isAdminRole,
  hasFeatureAccess,
  ACTION_TYPES,
  ACTION_FEATURE_MAP,
  workflowDefinitions,
  onClose,
}) => {
  const s = useMemo(() => createBulkStyles(theme, isRTL), [theme, isRTL]);
  const k = s.k;
  const lang = language === "ar" ? "ar" : "en";
  const [step, setStep] = useState(selectedAction ? "details" : "choose");
  const shellRef = useRef(null);
  const mainRef = useRef(null);
  const hideSalary = !isAdminView && !isAdminRole;
  const emp = selectedEmployee || {};
  const departmentName = emp.department?.name ?? emp.employeeType ?? null;

  const availableActions = useMemo(
    () =>
      ACTION_TYPES.filter((action) => {
        // Manager view: salary changes are admin / admin-level only.
        if (isActionBlockedForManagerView(action.id, { isAdminView, isAdminRole })) return false;
        if (!isAdminView || isAdminRole || !hasFeatureAccess) return true;
        const featureKey = ACTION_FEATURE_MAP[action.id] || null;
        return !featureKey || hasFeatureAccess("changesPage", featureKey);
      }),
    [ACTION_TYPES, ACTION_FEATURE_MAP, isAdminView, isAdminRole, hasFeatureAccess]
  );
  const actionConfig = ACTION_TYPES.find((a) => a.id === selectedAction);

  useEffect(() => {
    shellRef.current?.focus();
  }, []);

  useEffect(() => {
    mainRef.current?.scrollTo?.(0, 0);
  }, [step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isSubmitting, onClose]);

  const goTo = (next) => {
    setErrorMsg("");
    setStep(next);
  };

  const chooseAction = (id) => {
    setSelectedAction(id);
    goTo("details");
  };

  const idx = STEPS.indexOf(step);
  const Back = isRTL ? ArrowRight : ArrowLeft;
  const Next = isRTL ? ArrowLeft : ArrowRight;
  const locale = language === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const fmtDate = (v) => {
    if (!v) return null;
    const str = String(v).slice(0, 10);
    const [y, m, d] = str.split("-").map(Number);
    if (!y || !m || !d) return String(v);
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(y, m - 1, d));
  };
  const fmtMoney = (v) =>
    v === null || v === undefined || v === "" || Number.isNaN(Number(v))
      ? null
      : `${Number(v).toLocaleString(locale, { maximumFractionDigits: 2 })} ${t.egp}`;
  const arrow = isRTL ? "←" : "→";

  const approvalLabel = (id) =>
    getWorkflowApprovalLabel(
      id,
      workflowDefinitions,
      language,
      t,
      ACTION_TYPES.find((a) => a.id === id)?.approvalLevelKey,
      departmentName
    );
  // "Step A (Direct Manager) → Step B (Admin Level)" -> ["Step A (…)", "Step B (…)"]
  const approvalSteps = (id) =>
    String(approvalLabel(id) || "")
      .split(/\s*[→←]\s*/)
      .map((x) => x.trim())
      .filter(Boolean);

  // ── Form styling: quiet labels, soft fields (inputs match ReusableDropdown) ─
  const fieldBg = k.dark ? "rgba(255,255,255,0.035)" : "#ffffff";
  const fieldBase = {
    width: "100%",
    minHeight: 46,
    padding: "11px 14px",
    borderRadius: 10,
    border: `1px solid ${k.dark ? "rgba(148,163,184,0.22)" : "#d6dde6"}`,
    background: fieldBg,
    color: k.text,
    colorScheme: k.dark ? "dark" : "light",
    fontSize: 14.5,
    fontWeight: 400,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    textAlign: "start",
    direction: isRTL ? "rtl" : "ltr",
    transition: "border-color .15s ease, box-shadow .15s ease",
  };
  const sectionTitle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "0 0 12px",
    fontSize: 13,
    fontWeight: 800,
    color: k.muted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };
  const formStyles = {
    ...styles,
    formSections: { display: "flex", flexDirection: "column" },
    sectionTitle: { ...sectionTitle, marginTop: 4 },
    // Column counts come from the .nx-ea-grid-* rules below.
    formGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "12px 18px", marginBottom: 16 },
    notesGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "12px 18px" },
    controlGroup: { display: "flex", flexDirection: "column", gap: 7, minWidth: 0 },
    label: { display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 500, color: k.dark ? "#cbd5e1" : "#475569" },
    input: fieldBase,
    dateButton: { minHeight: 46, padding: "11px 14px", borderRadius: 10, fontSize: 14.5, fontWeight: 400, background: fieldBg },
    textarea: { ...fieldBase, minHeight: 82, height: 82, resize: "vertical", lineHeight: 1.55 },
  };

  // ── Shared bits ───────────────────────────────────────────────────────────
  const name = selectedEmployee ? getEmployeeName(selectedEmployee) : "";
  const panelCard = { padding: 12, borderRadius: 14, border: `1px solid ${k.border}`, background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff" };

  const errorBox = errorMsg ? (
    <div style={{ ...s.alert("error"), marginTop: 16 }} role="alert">
      <AlertCircle size={18} style={{ flexShrink: 0, color: "#ef4444", marginTop: 1 }} />
      <span>{getDisplayError(errorMsg, language)}</span>
    </div>
  ) : null;

  const stepHeading = (title, hint) => (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>{title}</h3>
      {hint && <p style={{ margin: "4px 0 0", fontSize: 14, color: k.muted }}>{hint}</p>}
    </div>
  );

  // ── Side panel ────────────────────────────────────────────────────────────
  const sidePanel = (
    <aside className="nx-ea-aside">
      <EmployeeSummaryCard employee={selectedEmployee} name={name} t={t} s={s} language={language} hideSalary={hideSalary} />

      {/* Chosen action + approval path */}
      {actionConfig && (
        <div style={{ ...panelCard, marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ ...s.actionIcon(actionConfig.color), width: 42, height: 42, borderRadius: 12, marginBottom: 0, flexShrink: 0 }}>
              <actionConfig.icon size={20} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12.5, color: k.muted, fontWeight: 600 }}>{t.actionType}</span>
                {step !== "choose" && !isSubmitting && (
                  <button
                    type="button"
                    onClick={() => goTo("choose")}
                    style={{ border: "none", background: "none", color: k.accent, fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0, fontFamily: "inherit" }}
                  >
                    {t.changeAction}
                  </button>
                )}
              </div>
              <div style={{ fontWeight: 800, fontSize: 15.5 }}>{t[actionConfig.labelKey]}</div>
            </div>
          </div>

          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${k.border}` }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: k.muted, marginBottom: 8 }}>{t.approvalPath}</div>
            {actionConfig.requiresApproval ? (
              <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {approvalSteps(actionConfig.id).map((label, i, all) => (
                  <li key={`${i}-${label}`} style={{ display: "flex", gap: 10, position: "relative", paddingBottom: i < all.length - 1 ? 14 : 0 }}>
                    {i < all.length - 1 && (
                      <span style={{ position: "absolute", insetInlineStart: 11, top: 24, bottom: 2, width: 2, background: k.border }} />
                    )}
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "rgba(245,158,11,0.15)",
                        color: k.dark ? "#fbbf24" : "#b45309",
                        fontSize: 12,
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45, paddingTop: 2 }}>{label}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: k.muted }}>
                <ShieldCheck size={16} style={{ color: "#10b981" }} />
                {t.noApprovalNeeded}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );

  // ── Step 1 · Choose ───────────────────────────────────────────────────────
  const renderChoose = () => (
    <>
      {/* The step bar already names this step ("Choose action"). */}
      {availableActions.length === 0 ? (
        <div style={s.alert("warning")}>{t.noActionsAllowed}</div>
      ) : (
        <div className="nx-ea-cards" role="radiogroup" aria-label={t.actionType}>
          {availableActions.map((action) => {
            const Icon = action.icon;
            const selected = selectedAction === action.id;
            return (
              <button
                key={action.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className="nxb-card"
                style={{
                  ...s.actionCard(selected, false, action.color),
                  padding: 14,
                  borderRadius: 16,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  height: "100%",
                }}
                onClick={() => chooseAction(action.id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{ ...s.actionIcon(action.color), width: 42, height: 42, borderRadius: 12, marginBottom: 0, flexShrink: 0 }}>
                    <Icon size={21} />
                  </div>
                  <div style={{ fontSize: 15.5, fontWeight: 800, lineHeight: 1.3 }}>{t[action.labelKey]}</div>
                </div>
                <div style={{ fontSize: 13.5, color: k.muted, lineHeight: 1.5, flex: 1 }}>{t[action.descriptionKey]}</div>
                {action.requiresApproval && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 6,
                      marginTop: 12,
                      paddingTop: 10,
                      borderTop: `1px solid ${k.border}`,
                      fontSize: 12.5,
                      color: k.muted,
                      lineHeight: 1.45,
                    }}
                  >
                    <ShieldCheck size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{approvalLabel(action.id)}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  // ── Step 2 · Details ──────────────────────────────────────────────────────
  const renderDetails = () => (
    <form
      id="nx-ea-form"
      className="nx-ea-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (onReview()) goTo("review");
      }}
    >
      <ActionFormFields
        t={t}
        styles={formStyles}
        theme={theme}
        isRTL={isRTL}
        language={language}
        selectedAction={selectedAction}
        formData={formData}
        handleInputChange={handleInputChange}
        handleDropdownChange={handleDropdownChange}
        departments={departments}
        departmentManagers={departmentManagers}
        selectedEmployee={selectedEmployee}
        ACTION_TYPES={ACTION_TYPES}
        workflowDefinitions={workflowDefinitions}
        hideSalary={hideSalary}
        showApprovalNote={false}
      />
      {errorBox}
    </form>
  );

  // ── Step 3 · Review ───────────────────────────────────────────────────────
  const reviewTiles = () => {
    const tiles = [];
    const change = (label, from, to) => {
      if (to === undefined || to === null || to === "" || String(to) === String(from ?? "")) return;
      tiles.push({ label, from: from || "—", to });
    };

    if (selectedAction === "suspend") {
      tiles.push({ label: t.suspensionStartDate, value: fmtDate(formData.suspensionStartDate) });
      tiles.push({ label: t.suspensionEndDate, value: fmtDate(formData.suspensionEndDate) });
    } else if (selectedAction === "terminate" || selectedAction === "resignation") {
      tiles.push({ label: t.lastWorkingDay, value: fmtDate(formData.lastWorkingDay) });
    } else {
      tiles.push({ label: t.effectiveDate, value: fmtDate(formData.effectiveDate) });
    }
    if (selectedAction === "rehire" && formData.rehireDate) tiles.push({ label: t.rehireDate, value: fmtDate(formData.rehireDate) });

    OPTION_FIELDS.forEach(({ field, labelKey, options }) => {
      const v = formData[field];
      if (v) tiles.push({ label: t[labelKey], value: t[options[v]] || v });
    });

    change(t.position, emp.position ?? emp.employeeType, formData.newPosition);
    change(t.department, departmentName, formData.newDepartment);
    if (selectedAction === "transfer") change(t.directManager, emp.firstLineManagerName, formData.reportsTo);

    if (!hideSalary && SALARY_ACTIONS.includes(selectedAction)) {
      const salaryRows =
        selectedAction === "salary_change"
          ? [
              [lang === "ar" ? "صافي الراتب" : "Net salary", emp.netSalary, formData.newSalary],
              [lang === "ar" ? "الراتب الإجمالي" : "Gross salary", emp.grossSalary, formData.newGrossSalary],
              [lang === "ar" ? "الأجر التأميني" : "Insurance salary", emp.insuranceSalary, formData.newInsuranceSalary],
            ]
          : [[lang === "ar" ? "صافي الراتب" : "Net salary", emp.netSalary, formData.newSalary]];
      salaryRows.forEach(([label, prev, next]) => {
        if (next === "" || next === null || next === undefined || Number.isNaN(Number(next))) return;
        if (prev !== null && prev !== undefined && Math.abs(Number(next) - Number(prev)) < 0.01) return;
        tiles.push({ label, from: fmtMoney(prev) || "—", to: fmtMoney(next), up: Number(next) >= Number(prev ?? 0) });
      });
    }
    if (formData.severanceAmount) tiles.push({ label: t.severancePackage, value: fmtMoney(formData.severanceAmount) });
    return tiles;
  };

  const renderReview = () => {
    const tiles = reviewTiles();
    const tile = { padding: "14px 16px", borderRadius: 14, border: `1px solid ${k.border}`, background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff", minWidth: 0 };
    const tileLabel = { fontSize: 13, fontWeight: 600, color: k.muted, marginBottom: 6 };
    const notes = [
      [t.reasonForAction, formData.reason],
      [t.approverNotes, formData.approverNotes],
      selectedAction === "terminate" || selectedAction === "resignation" ? [t.terminationReason, formData.terminationReason] : null,
      selectedAction === "suspend" ? [t.suspensionReason, formData.suspensionReason] : null,
    ].filter((x) => x && x[1]);
    return (
      <>
        {stepHeading(t.confirmAction, t.reviewBeforeConfirm)}

        <h4 style={sectionTitle}>{t.changesTitle}</h4>
        <div className="nx-ea-tiles">
          {tiles.map((r) => (
            <div key={r.label} style={tile}>
              <div style={tileLabel}>{r.label}</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>
                {r.value !== undefined ? (
                  r.value || "—"
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: k.muted, fontWeight: 600, fontSize: 14, textDecoration: "line-through" }}>{r.from}</span>
                    <span style={{ color: k.muted, fontSize: 14 }}>{arrow}</span>
                    <span style={{ color: r.up === undefined ? k.text : r.up ? "#10b981" : "#ef4444" }}>{r.to}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {notes.length > 0 && (
          <>
            <h4 style={{ ...sectionTitle, marginTop: 22 }}>{t.reasonAndNotes}</h4>
            <div className="nx-ea-notes">
              {notes.map(([label, text]) => (
                <div key={label} style={tile}>
                  <div style={tileLabel}>{label}</div>
                  <div style={{ fontSize: 14.5, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{text}</div>
                </div>
              ))}
            </div>
          </>
        )}
        {errorBox}
      </>
    );
  };

  // ── Footer ────────────────────────────────────────────────────────────────
  const footer = () => {
    if (step === "choose") {
      return (
        <>
          <span />
          <div style={s.footerGroup}>
            <Btn s={s} variant="tertiary" onClick={onClose}>{t.cancel}</Btn>
            <Btn s={s} variant="primary" disabled={!selectedAction} onClick={() => goTo("details")}>
              {t.next} <Next size={16} />
            </Btn>
          </div>
        </>
      );
    }
    if (step === "details") {
      return (
        <>
          <div style={s.footerGroup}>
            <Btn s={s} variant="secondary" icon={Back} onClick={() => goTo("choose")}>{t.back}</Btn>
          </div>
          <div style={s.footerGroup}>
            <Btn s={s} variant="tertiary" onClick={onClose}>{t.cancel}</Btn>
            <Btn s={s} variant="primary" type="submit" form="nx-ea-form">
              {t.reviewStep} <Next size={16} />
            </Btn>
          </div>
        </>
      );
    }
    return (
      <>
        <div style={s.footerGroup}>
          <Btn s={s} variant="secondary" icon={Back} disabled={isSubmitting} onClick={() => goTo("details")}>{t.back}</Btn>
        </div>
        <div style={s.footerGroup}>
          <Btn s={s} variant="tertiary" disabled={isSubmitting} onClick={onClose}>{t.cancel}</Btn>
          <Btn s={s} variant="primary" icon={Send} busy={isSubmitting} onClick={onConfirm}>
            {isSubmitting ? t.processing : t.confirmSubmit}
          </Btn>
        </div>
      </>
    );
  };

  const stepLabels = { choose: t.chooseActionStep, details: t.detailsStep, review: t.reviewStep };

  return (
    <div
      className="nxb-overlay"
      style={s.overlay}
      onMouseDown={(e) => e.target === e.currentTarget && !isSubmitting && onClose()}
    >
      <style>{BULK_CSS}</style>
      <style>{`
        .nx-ea-body { flex: 1; min-height: 0; display: flex; }
        .nx-ea-aside {
          width: 340px; flex-shrink: 0; box-sizing: border-box; overflow-y: auto;
          padding: 14px 14px 10px; background: ${k.subtle}; border-inline-end: 1px solid ${k.border};
        }
        .nx-ea-main { flex: 1; min-width: 0; overflow-y: auto; padding: 0 28px 16px; }
        .nx-ea-stepper {
          display: flex; align-items: center; gap: 12px;
          margin: 0 -28px 16px; padding: 10px 28px;
          background: ${k.subtle}; border-bottom: 1px solid ${k.border};
        }
        .nx-ea-step { display: flex; align-items: center; gap: 10px; font-size: 14px; font-weight: 600; color: ${k.muted}; white-space: nowrap; }
        .nx-ea-step[data-state="active"] { color: ${k.text}; font-weight: 800; }
        .nx-ea-step[data-state="done"] { color: ${k.text}; }
        .nx-ea-step-line { flex: 1; min-width: 24px; height: 2px; border-radius: 2px; background: ${k.border}; }
        .nx-ea-step-line[data-done="true"] { background: #10b981; }
        .nx-ea-cards { display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr)); }
        .nx-ea-tiles { display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr)); }
        .nx-ea-notes { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr)); }
        .nx-ea-form .enhanced-input-focus:focus { transform: none; border-color: #2e7cba; box-shadow: 0 0 0 3px ${k.dark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.18)"}; }
        .nx-ea-form label svg { display: none; }
        .nx-ea-form input::placeholder, .nx-ea-form textarea::placeholder { color: ${k.dark ? "#64748b" : "#94a3b8"}; opacity: 1; }
        ${k.dark ? `.nx-ea-form input:not([type="checkbox"]):not([aria-hidden="true"]) { background-color: ${"rgba(255,255,255,0.035)"} !important; }` : ""}
        .nx-ea-form .enhanced-input-focus:disabled { background: ${k.dark ? "rgba(15,23,42,0.3)" : "#f1f5f9"} !important; }
        @media (min-width: 641px) {
          .nx-ea-grid-main, .nx-ea-grid-notes { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .nx-ea-grid-main > div:has(> textarea) { grid-column: 1 / -1; order: 10; }
        }
        @media (min-width: 1200px) {
          .nx-ea-cards { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .nx-ea-tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .nx-ea-grid-main { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 900px) {
          .nx-ea-body { flex-direction: column; overflow-y: auto; }
          .nx-ea-aside { width: auto; overflow: visible; border-inline-end: none; border-bottom: 1px solid ${k.border}; }
          .nx-ea-main { overflow: visible; }
          .nx-ea-step-label { display: none; }
        }
      `}</style>
      <div
        ref={shellRef}
        className="nxb-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-ea-title"
        tabIndex={-1}
        style={{ ...s.shell, width: "min(1320px, 100%)", height: "min(820px, 95vh)", position: "relative", outline: "none" }}
      >
        <div className="nxb-header" style={{ ...s.header, padding: "12px 28px", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0, flex: 1 }}>
            <div style={{ ...s.headerIcon, width: 46, height: 46, borderRadius: 13 }}><Zap size={22} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 id="nx-ea-title" style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: "-0.01em" }}>{t.employeeAction}</h2>
            </div>
          </div>

          <button
            type="button"
            className="nxb-btn"
            style={s.iconButton}
            aria-label={t.cancel}
            title={t.cancel}
            disabled={isSubmitting}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        <div className="nx-ea-body">
          {sidePanel}
          <div ref={mainRef} className="nx-ea-main">
            <nav className="nx-ea-stepper" aria-label={t.employeeAction}>
              {STEPS.map((key, i) => {
                const state = i < idx ? "done" : i === idx ? "active" : "todo";
                return (
                  <React.Fragment key={key}>
                    <div className="nx-ea-step" data-state={state} aria-current={state === "active" ? "step" : undefined}>
                      <span style={{ ...s.stepDot(state), width: 26, height: 26, fontSize: 12 }}>
                        {state === "done" ? <Check size={13} strokeWidth={3} /> : i + 1}
                      </span>
                      <span className="nx-ea-step-label">{stepLabels[key]}</span>
                    </div>
                    {i < STEPS.length - 1 && <span className="nx-ea-step-line" data-done={i < idx ? "true" : "false"} />}
                  </React.Fragment>
                );
              })}
            </nav>
            {step === "choose" && renderChoose()}
            {step === "details" && actionConfig && renderDetails()}
            {step === "review" && actionConfig && renderReview()}
          </div>
        </div>

        <div className="nxb-footer" style={{ ...s.footer, padding: "12px 28px" }}>{footer()}</div>
      </div>
    </div>
  );
};

export default ActionModal;
